import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EvaluationImagesTransferred, EvaluationService, ImageTransferResult } from '@core/data';
import { tap } from 'rxjs/operators';
import { ClearImageSelection, ToggleImageSelection, TransferImages, UndoImageTransfer } from './image-move.state-actions';
import { DEFAULTS, ImageMoveStateModel } from './image-move.state-model';

@State({
    name: 'image_move',
    defaults: DEFAULTS
})
@Injectable()
export class ImageMoveState {

    @Selector()
    static sourceEvalKey(state: ImageMoveStateModel) {
        return state.sourceEvalKey;
    }

    @Selector()
    static imageType(state: ImageMoveStateModel) {
        return state.imageType;
    }

    @Selector()
    static selectedKeys(state: ImageMoveStateModel) {
        return state.selectedKeys;
    }

    @Selector()
    static selectedData(state: ImageMoveStateModel) {
        return state.selectedData;
    }

    @Selector()
    static transferResults(state: ImageMoveStateModel) {
        return state.transferResults;
    }

    constructor(private _evalService: EvaluationService) { }

    @Action(ToggleImageSelection)
    toggleImageSelection(ctx: StateContext<ImageMoveStateModel>, action: ToggleImageSelection) {

        const image = action.image;
        const imageType = action.imageType;

        if (!image || !image.key || !image.evalKey) return;

        const state = ctx.getState();

        const startsFreshBatch = state.selectedKeys.length > 0
            && (state.sourceEvalKey !== image.evalKey || state.imageType !== imageType);

        if (startsFreshBatch) {
            ctx.patchState({
                sourceEvalKey: image.evalKey,
                imageType,
                selectedKeys: [image.key],
                selectedData: [image],
                transferResults: {},
            });
            return;
        }

        const alreadySelected = state.selectedKeys.includes(image.key);

        const selectedKeys = alreadySelected
            ? state.selectedKeys.filter(key => key !== image.key)
            : [...state.selectedKeys, image.key];

        const selectedData = alreadySelected
            ? state.selectedData.filter(img => img.key !== image.key)
            : [...state.selectedData, image];

        ctx.patchState({
            sourceEvalKey: selectedKeys.length > 0 ? image.evalKey : null,
            imageType: selectedKeys.length > 0 ? imageType : null,
            selectedKeys,
            selectedData,
            transferResults: {},
        });
    }

    @Action(ClearImageSelection)
    clearImageSelection(ctx: StateContext<ImageMoveStateModel>) {
        ctx.patchState({
            sourceEvalKey: null,
            imageType: null,
            selectedKeys: [],
            selectedData: [],
        });
    }

    @Action(TransferImages)
    transferImages(ctx: StateContext<ImageMoveStateModel>, action: TransferImages) {

        if (!action.imageKeys || action.imageKeys.length === 0) return;

        return this._evalService.transferImages(action.imageKeys, action.destinationEvalKey)
            .pipe(tap(response => {

                const results = (response && response.results) || [];

                const resultsByKey: { [imageKey: string]: ImageTransferResult } = {};
                results.forEach(result => resultsByKey[result.imageKey] = result);

                action.imageKeys.forEach(key => {
                    if (!resultsByKey[key]) {
                        resultsByKey[key] = { imageKey: key, status: 'failed', reason: 'No response received for this image' };
                    }
                });

                const succeededKeys = action.imageKeys.filter(key => resultsByKey[key].status === 'success');

                const state = ctx.getState();

                ctx.patchState({
                    transferResults: resultsByKey,
                    selectedKeys: state.selectedKeys.filter(key => !succeededKeys.includes(key)),
                    selectedData: state.selectedData.filter(img => !succeededKeys.includes(img.key)),
                });

                if (succeededKeys.length > 0) {
                    ctx.dispatch(new EvaluationImagesTransferred([state.sourceEvalKey, action.destinationEvalKey]));
                }
            }));
    }

    @Action(UndoImageTransfer)
    undoImageTransfer(ctx: StateContext<ImageMoveStateModel>, action: UndoImageTransfer) {

        if (!action.imageKeys || action.imageKeys.length === 0) return;

        return this._evalService.transferImages(action.imageKeys, action.sourceEvalKey)
            .pipe(tap(response => {

                const results = (response && response.results) || [];

                const resultsByKey: { [imageKey: string]: ImageTransferResult } = {};
                results.forEach(result => resultsByKey[result.imageKey] = result);

                action.imageKeys.forEach(key => {
                    if (!resultsByKey[key]) {
                        resultsByKey[key] = { imageKey: key, status: 'failed', reason: 'No response received for this image' };
                    }
                });

                ctx.patchState({
                    transferResults: resultsByKey,
                });

                const succeededKeys = action.imageKeys.filter(key => resultsByKey[key].status === 'success');

                if (succeededKeys.length > 0) {
                    const affectedEvalKeys = [action.sourceEvalKey, action.previousEvalKey].filter(Boolean);
                    ctx.dispatch(new EvaluationImagesTransferred(affectedEvalKeys));
                }
            }));
    }
}
